import { useState } from "react";
import { useAuth, normalizePhone } from "@mydailydirect/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Input,
  Label,
  PageHeader,
  useToast,
  Dialog,
  DialogHeader,
  DialogFooter,
  DocumentUpload,
  ImageLightboxModal,
  parseRetailerKyc,
  serializeRetailerKyc,
  SUPPORTED_LANGUAGES,
  getLanguageLabel,
} from "@mydailydirect/ui";
import {
  Shield,
  Activity,
  ShoppingBag,
  Hash,
  MapPin,
  CreditCard,
  Store,
  LogOut,
  Phone,
  Building2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Navigation,
  UploadCloud,
  Edit3,
  ExternalLink,
  ZoomIn,
  MessageSquare,
  Languages,
  Send,
  FileText,
} from "lucide-react";

export function ProfilePage() {
  const { session, logout } = useAuth();
  const client = useAuth().client;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query users to match linked retailerId
  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => client.users.list(),
    enabled: Boolean(session),
  });

  // Query retailers to match shop details
  const retailersQuery = useQuery({
    queryKey: ["retailers"],
    queryFn: () => client.retailers.list(),
    enabled: Boolean(session),
  });

  const me = usersQuery.data?.find((u) => u.username === session?.userId);
  const sessionDigits = normalizePhone(session?.phone || session?.userId);

  const matchedRetailer = me?.retailerId
    ? retailersQuery.data?.find((r) => r.id === me.retailerId)
    : retailersQuery.data?.find(
        (r) =>
          (sessionDigits.length > 0 && normalizePhone(r.phone) === sessionDigits) ||
          (session?.businessName && r.name.toLowerCase() === session.businessName.toLowerCase())
      );

  const activeRetailer = matchedRetailer || null;
  const activeRetailerId = activeRetailer?.id ?? null;

  // Query catalog SKUs for stats
  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    enabled: session?.roles.includes("TRADER") || session?.roles.includes("ADMIN"),
  });

  // Query orders for stats
  const ordersQuery = useQuery({
    queryKey: ["orders"],
    queryFn: () => client.orders.list(),
    enabled: session?.roles.includes("RETAILER") || session?.roles.includes("ADMIN"),
  });

  const myOrdersCount =
    ordersQuery.data?.filter((o) => o.retailerId === activeRetailerId).length || 0;

  // WhatsApp opt-in query & state
  const optInQuery = useQuery({
    queryKey: ["whatsapp-opt-in", activeRetailerId],
    queryFn: () => (activeRetailerId ? client.whatsapp.getOptIn(activeRetailerId) : null),
    enabled: Boolean(activeRetailerId),
  });

  const [optedIn, setOptedIn] = useState<boolean | null>(null);
  const [prefLang, setPrefLang] = useState<string | null>(null);

  const parsedActiveKyc = parseRetailerKyc(activeRetailer?.photoUrl);
  const currentOptedIn = optedIn !== null ? optedIn : (optInQuery.data?.optedIn ?? true);
  const currentLang =
    prefLang !== null
      ? prefLang
      : parsedActiveKyc.languagePreference || optInQuery.data?.languageCode || "";

  const updateOptInMutation = useMutation({
    mutationFn: async (body: {
      retailerId: number;
      phone: string;
      optedIn: boolean;
      preferredLanguage: string;
    }) => {
      if (activeRetailer) {
        const updatedPhotoUrl = serializeRetailerKyc({
          ...parseRetailerKyc(activeRetailer.photoUrl),
          languagePreference: body.preferredLanguage || undefined,
        });
        await client.retailers.update(body.retailerId, {
          ...activeRetailer,
          photoUrl: updatedPhotoUrl,
        });
      }
      if (body.preferredLanguage) {
        return client.whatsapp.updateOptIn(body);
      }
      return null;
    },
    onSuccess: () => {
      toast({
        title: "Preferences Saved",
        description: "Language & WhatsApp alert settings updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["retailers"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-opt-in", activeRetailerId] });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to save",
        description: err.message || "Could not update WhatsApp settings.",
        variant: "error",
      });
    },
  });

  const testWhatsAppMutation = useMutation({
    mutationFn: (body: { retailerId: number; text: string }) =>
      client.whatsapp.sendTest(body),
    onSuccess: () => {
      toast({
        title: "Test Alert Sent",
        description: "A test WhatsApp message was sent to your phone.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Delivery Failed",
        description: err.message || "Could not send test message.",
        variant: "error",
      });
    },
  });


  const initials = session?.fullName
    ? session.fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  const primaryRole = session?.roles[0] || "User";

  // Verification & Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const [editName, setEditName] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editAddressLine, setEditAddressLine] = useState("");
  const [editLatitude, setEditLatitude] = useState<number | null>(null);
  const [editLongitude, setEditLongitude] = useState<number | null>(null);
  const [editShopPhoto, setEditShopPhoto] = useState<string>("");
  const [editDocUrl, setEditDocUrl] = useState<string>("");
  const [editLanguagePreference, setEditLanguagePreference] = useState<string>("");
  const [editGstNumber, setEditGstNumber] = useState("");
  const [locating, setLocating] = useState(false);
  const [locatingMsg, setLocatingMsg] = useState("");

  const openEditModal = () => {
    if (!activeRetailer) return;
    const parsed = parseRetailerKyc(activeRetailer.photoUrl);
    setEditName(activeRetailer.name || "");
    setEditCity(activeRetailer.city || "Bengaluru");
    setEditAddressLine(activeRetailer.addressLine || "");
    setEditLatitude(activeRetailer.latitude ?? null);
    setEditLongitude(activeRetailer.longitude ?? null);
    setEditShopPhoto(parsed.shopPhoto || "");
    setEditDocUrl(parsed.docUrl || "");
    setEditLanguagePreference(parsed.languagePreference || "");
    setEditGstNumber(activeRetailer.gstNumber || "");
    setLocatingMsg("");
    setIsEditModalOpen(true);
  };

  const handleCaptureGPS = () => {
    if (!navigator.geolocation) {
      setLocatingMsg("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    setLocatingMsg("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEditLatitude(pos.coords.latitude);
        setEditLongitude(pos.coords.longitude);
        setLocating(false);
        setLocatingMsg(`GPS captured: ${pos.coords.latitude.toFixed(5)}°, ${pos.coords.longitude.toFixed(5)}°`);
      },
      () => {
        setLocating(false);
        setLocatingMsg("Could not detect location. Please allow browser location access.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const updateProfileMutation = useMutation({
    mutationFn: async () => {
      if (!activeRetailerId) throw new Error("No linked shop record found to update.");
      const currentStatus = activeRetailer?.status;
      // Resubmitting resets REJECTED to PENDING so admin re-evaluates
      const nextStatus = currentStatus === "REJECTED" ? "PENDING" : currentStatus || "PENDING";
      const photoPayload = serializeRetailerKyc({
        shopPhoto: editShopPhoto,
        docUrl: editDocUrl,
        languagePreference: editLanguagePreference || undefined,
      });

      return client.retailers.update(activeRetailerId, {
        id: activeRetailerId,
        name: editName.trim() || activeRetailer!.name,
        phone: activeRetailer!.phone,
        city: editCity.trim() || activeRetailer!.city,
        addressLine: editAddressLine.trim() || activeRetailer!.addressLine,
        latitude: editLatitude,
        longitude: editLongitude,
        photoUrl: photoPayload || activeRetailer!.photoUrl,
        gstNumber: editGstNumber.trim() || null,
        status: nextStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["retailers"] });
      queryClient.invalidateQueries({ queryKey: ["retailers", activeRetailerId] });
      setIsEditModalOpen(false);
      toast({
        title: "Shop Verification Updated! 🎉",
        description: "Your business details & documents have been submitted for verification.",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to update profile",
        description: err instanceof Error ? err.message : "Network error",
        variant: "error",
      });
    },
  });

  const handleLogout = () => {
    logout();
    toast({
      title: "Signed Out",
      description: "You have been safely signed out of your session.",
      variant: "info",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      <PageHeader
        title="My Profile & Security"
        description="Manage your account profile, business details, and active session permissions."
        actions={
          <Button variant="destructive" size="sm" onClick={handleLogout} className="font-bold gap-1.5">
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        }
      />

      {/* Main Avatar & Credential Panel */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1 border-border relative overflow-hidden bg-card">
          <CardContent className="flex flex-col items-center p-6 text-center pt-8 space-y-3">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary text-2xl font-black text-primary-foreground shadow-md relative border border-primary/30">
              {initials}
              <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white border-2 border-background">
                <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold tracking-tight text-foreground">{session?.fullName}</h2>
              <p className="text-xs text-muted-foreground font-mono">@{session?.userId}</p>
            </div>

            {session?.businessName && (
              <div className="flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                <Building2 className="h-3.5 w-3.5" />
                <span>{session.businessName}</span>
              </div>
            )}

            <div className="pt-2 flex flex-wrap justify-center gap-1.5">
              {session?.roles.map((role) => (
                <Badge key={role} variant="default" className="px-2.5 py-0.5 text-xs font-extrabold uppercase tracking-wider">
                  {role}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security & System Info */}
        <Card className="lg:col-span-2 border-border bg-card">
          <CardHeader className="pb-3 border-b border-border">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" /> Account Security & Session Assertions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border bg-muted/30 p-3.5 space-y-1">
                <span className="text-2xs text-muted-foreground uppercase font-bold tracking-wider flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-primary" /> Role Persona
                </span>
                <p className="text-base font-extrabold text-foreground uppercase">{session?.accountType || primaryRole}</p>
              </div>

              <div className="rounded-xl border border-border bg-muted/30 p-3.5 space-y-1">
                <span className="text-2xs text-muted-foreground uppercase font-bold tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Account Status
                </span>
                <p className="text-base font-extrabold text-emerald-600 dark:text-emerald-400">ACTIVE & VERIFIED</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(session?.phone || session?.phoneNumber) && (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-background text-xs">
                  <Phone className="h-4 w-4 text-primary shrink-0" />
                  <div>
                    <span className="text-muted-foreground block text-2xs font-semibold">Phone Number</span>
                    <span className="font-bold text-foreground">{session.phone || session.phoneNumber}</span>
                  </div>
                </div>
              )}

              {(session?.orgId || session?.organizationId || session?.tenantId) && (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-background text-xs">
                  <Building2 className="h-4 w-4 text-primary shrink-0" />
                  <div>
                    <span className="text-muted-foreground block text-2xs font-semibold">Organization ID (OrgID)</span>
                    <span className="font-mono font-bold text-foreground">
                      {session.orgId || session.organizationId || session.tenantId}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
              <h4 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Hash className="h-4 w-4 text-primary" /> Per-Request Assertion Cryptography
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your session is secured using HMAC assertion signatures stamped into every API request header.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Linked Shop Verification Center (For Retailers) */}
      {activeRetailer && (
        <div className="space-y-4">
          {/* Status Callout Banner */}
          {activeRetailer.status === "APPROVED" && (
            <div className="p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-emerald-950 dark:text-emerald-200">
                  Verified Kirana Partner — Catalog Ordering Unlocked
                </h3>
                <p className="text-xs text-emerald-800/90 dark:text-emerald-300/90 leading-relaxed">
                  Your shop location and business documents are fully verified by MyDailyDirect. You have authorized wholesale ordering access.
                </p>
              </div>
            </div>
          )}

          {activeRetailer.status === "PENDING" && (
            <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-amber-950 dark:text-amber-200">
                    Shop Verification In Progress — Order Placement Locked
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openEditModal}
                    className="text-xs font-bold border-amber-500/40 text-amber-900 dark:text-amber-200 hover:bg-amber-500/20 gap-1.5 shrink-0"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Upload / Update Details
                  </Button>
                </div>
                <p className="text-xs text-amber-800/90 dark:text-amber-300/90 leading-relaxed">
                  Your shop registration is pending review by MyDailyDirect Admin. Once approved, catalog checkout will be unlocked. Please ensure your shop address, GPS pin, and shop document/board photo are uploaded below to expedite approval.
                </p>
              </div>
            </div>
          )}

          {activeRetailer.status === "REJECTED" && (
            <div className="p-4 rounded-xl border border-destructive/40 bg-destructive/10 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-destructive">
                    Action Required: Verification Incomplete or Rejected
                  </h3>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={openEditModal}
                    className="text-xs font-bold gap-1.5 shrink-0"
                  >
                    <UploadCloud className="h-3.5 w-3.5" /> Resubmit Verification
                  </Button>
                </div>
                <p className="text-xs text-destructive/90 leading-relaxed">
                  Your shop verification requires additional information. Please update your shop address, capture your GPS location, and re-upload a clear shop board / business document below to resubmit.
                </p>
              </div>
            </div>
          )}

          {/* Shop Details Card */}
          <Card className="border border-border bg-card">
            <CardHeader className="pb-3 border-b border-border flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Store className="h-4 w-4 text-primary" /> Shop & Verification Details
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    activeRetailer.status === "APPROVED"
                      ? "success"
                      : activeRetailer.status === "REJECTED"
                      ? "destructive"
                      : "warning"
                  }
                  className="text-xs font-extrabold uppercase"
                >
                  {activeRetailer.status}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openEditModal}
                  className="text-xs font-bold gap-1.5 h-8"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Edit Details & KYC
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {/* Shop Photo / KYC Document Thumbnail */}
                <div className="md:col-span-1 space-y-2">
                  <span className="text-2xs font-bold text-muted-foreground uppercase tracking-wider block">
                    Shop Front & KYC Proofs
                  </span>
                  {(() => {
                    const { shopPhoto, docUrl } = parseRetailerKyc(activeRetailer.photoUrl);
                    if (!shopPhoto && !docUrl) {
                      return (
                        <div
                          onClick={openEditModal}
                          className="rounded-xl border-2 border-dashed border-border p-6 flex flex-col items-center justify-center gap-2 text-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/10 h-44"
                        >
                          <UploadCloud className="h-8 w-8 text-muted-foreground/60" />
                          <span className="text-xs font-bold text-foreground">No Proofs Uploaded</span>
                          <span className="text-2xs text-primary font-semibold hover:underline">
                            + Upload Shop Photo & Documents
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {shopPhoto && (
                          <div
                            className="relative rounded-xl overflow-hidden border border-border group bg-muted/20 cursor-pointer h-28 flex items-center justify-center"
                            onClick={() => setIsLightboxOpen(true)}
                            title="Click to enlarge photo"
                          >
                            <img
                              src={shopPhoto}
                              alt={activeRetailer.name}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-bold">
                              <ZoomIn className="h-4 w-4" /> Click to Enlarge
                            </div>
                            <div className="absolute bottom-1.5 left-1.5 bg-black/75 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-xs">
                              <Store className="h-3 w-3" /> Shopfront
                            </div>
                          </div>
                        )}
                        {docUrl && (
                          docUrl.startsWith("data:application/pdf") || docUrl.toLowerCase().includes(".pdf") ? (
                            <div
                              className="rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-2 cursor-pointer hover:bg-primary/10 transition-colors"
                              onClick={openEditModal}
                              title="Click to view or replace KYC document"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-6 w-6 text-primary shrink-0" />
                                <div>
                                  <p className="text-xs font-bold text-foreground">KYC Document (PDF)</p>
                                  <p className="text-[10px] text-muted-foreground">GST / Trade License</p>
                                </div>
                              </div>
                              <span className="text-2xs text-primary font-semibold underline">View</span>
                            </div>
                          ) : (
                            <div
                              className="relative rounded-xl overflow-hidden border border-border group bg-muted/20 cursor-pointer h-24 flex items-center justify-center"
                              onClick={openEditModal}
                            >
                              <img
                                src={docUrl}
                                alt="KYC Document"
                                className="w-full h-full object-contain"
                              />
                              <div className="absolute bottom-1.5 left-1.5 bg-black/75 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-xs">
                                <FileText className="h-3 w-3" /> Certificate
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Key Shop Info Details */}
                <div className="md:col-span-2 space-y-3">
                  <div>
                    <h3 className="text-lg font-black text-foreground">{activeRetailer.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Phone className="h-3 w-3 text-muted-foreground" /> {activeRetailer.phone}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
                    {/* Address */}
                    <div className="p-3 border border-border rounded-xl bg-muted/20 space-y-1">
                      <div className="flex items-center gap-1.5 text-muted-foreground font-semibold text-2xs uppercase">
                        <MapPin className="h-3.5 w-3.5 text-primary" /> Delivery Address
                      </div>
                      <p className="font-bold text-foreground leading-snug">
                        {activeRetailer.addressLine || "No detailed address specified"}
                      </p>
                      <p className="text-2xs text-muted-foreground">{activeRetailer.city || "Bengaluru"}</p>
                    </div>

                    {/* GPS Coordinates */}
                    <div className="p-3 border border-border rounded-xl bg-muted/20 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-muted-foreground font-semibold text-2xs uppercase">
                          <Navigation className="h-3.5 w-3.5 text-primary" /> Shop GPS Pin
                        </span>
                        {activeRetailer.latitude && activeRetailer.longitude && (
                          <a
                            href={`https://www.google.com/maps?q=${activeRetailer.latitude},${activeRetailer.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-2xs font-bold text-primary hover:underline flex items-center gap-0.5"
                          >
                            <ExternalLink className="h-3 w-3" /> Map
                          </a>
                        )}
                      </div>
                      {activeRetailer.latitude && activeRetailer.longitude ? (
                        <p className="font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400">
                          {activeRetailer.latitude.toFixed(5)}°, {activeRetailer.longitude.toFixed(5)}°
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold">
                          Not Captured · Set in Edit
                        </p>
                      )}
                      <p className="text-2xs text-muted-foreground">Used for delivery navigation</p>
                    </div>

                    {/* GSTIN */}
                    <div className="p-3 border border-border rounded-xl bg-muted/20 space-y-1">
                      <div className="flex items-center gap-1.5 text-muted-foreground font-semibold text-2xs uppercase">
                        <CreditCard className="h-3.5 w-3.5 text-primary" /> GSTIN / Tax ID
                      </div>
                      <p className="font-mono text-xs font-bold text-foreground">
                        {activeRetailer.gstNumber || "Unregistered (Kirana)"}
                      </p>
                      <p className="text-2xs text-muted-foreground">B2B Tax Invoice registration</p>
                    </div>

                    {/* Status */}
                    <div className="p-3 border border-border rounded-xl bg-muted/20 space-y-1">
                      <div className="flex items-center gap-1.5 text-muted-foreground font-semibold text-2xs uppercase">
                        <Shield className="h-3.5 w-3.5 text-primary" /> Verification Stage
                      </div>
                      <p className="text-xs font-black uppercase text-foreground">
                        {activeRetailer.status === "APPROVED"
                          ? "Verified & Active"
                          : activeRetailer.status === "REJECTED"
                          ? "Action Required"
                          : "Under Admin Review"}
                      </p>
                      <p className="text-2xs text-muted-foreground">Reviewed by MyDailyDirect Ops</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* WhatsApp Notifications & Language Preferences Card */}
      {activeRetailer && (
        <Card className="border-border">
          <CardHeader className="pb-3 border-b border-border/40">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2">
                    WhatsApp Order Updates & Digital Invoices
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Get instant order confirmations, next-day morning delivery tracking, and GST tax invoices.
                  </p>
                </div>
              </div>
              <Badge variant={currentOptedIn ? "success" : "secondary"}>
                {currentOptedIn ? "WhatsApp Alerts Active" : "Alerts Paused"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Languages className="h-3.5 w-3.5 text-primary" /> Preferred Message Language
                  </span>
                  <span className="text-2xs text-muted-foreground font-semibold">
                    {getLanguageLabel(currentLang)}
                  </span>
                </Label>
                <select
                  value={currentLang}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    setPrefLang(newLang);
                    if (activeRetailerId) {
                      updateOptInMutation.mutate({
                        retailerId: activeRetailerId,
                        phone: activeRetailer.phone || "",
                        optedIn: currentOptedIn,
                        preferredLanguage: newLang,
                      });
                    }
                  }}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- Select Preferred Language (Not Set) --</option>
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <p className="text-2xs text-muted-foreground">
                  All automated push-carts, invoices and delivery updates will be delivered in this language.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-primary" /> Delivery Alerts Phone Number
                </Label>
                <Input
                  disabled
                  value={activeRetailer.phone || ""}
                  className="h-10 text-sm font-mono bg-muted/40"
                />
                <p className="text-2xs text-muted-foreground">
                  Linked to your verified retailer business account.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-border/40">
              <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-foreground">
                <input
                  type="checkbox"
                  checked={currentOptedIn}
                  onChange={(e) => {
                    const newVal = e.target.checked;
                    setOptedIn(newVal);
                    if (activeRetailerId) {
                      updateOptInMutation.mutate({
                        retailerId: activeRetailerId,
                        phone: activeRetailer.phone || "",
                        optedIn: newVal,
                        preferredLanguage: currentLang,
                      });
                    }
                  }}
                  className="rounded border-input text-primary h-4 w-4 focus:ring-primary"
                />
                Receive automated order & push-cart alerts on WhatsApp
              </label>

              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs w-full sm:w-auto"
                disabled={testWhatsAppMutation.isPending || !activeRetailerId}
                onClick={() => {
                  if (activeRetailerId) {
                    testWhatsAppMutation.mutate({
                      retailerId: activeRetailerId,
                      text: "Namaskara from My Daily Direct! Your WhatsApp notifications are connected and working.",
                    });
                  }
                }}
              >
                <Send className="h-3.5 w-3.5 text-emerald-600" />
                {testWhatsAppMutation.isPending ? "Sending..." : "Send Test WhatsApp"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Activity Analytics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {session?.roles.includes("RETAILER") && (
          <Card className="p-5 flex flex-col items-center justify-center text-center border-border">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <span className="text-xs text-muted-foreground font-semibold block">Orders Placed</span>
            <p className="text-2xl font-black text-foreground mt-0.5">{myOrdersCount}</p>
          </Card>
        )}

        {(session?.roles.includes("TRADER") || session?.roles.includes("ADMIN")) && (
          <Card className="p-5 flex flex-col items-center justify-center text-center border-border">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <span className="text-xs text-muted-foreground font-semibold block">Active SKUs</span>
            <p className="text-2xl font-black text-foreground mt-0.5">{skusQuery.data?.length || 0}</p>
          </Card>
        )}

        {session?.roles.includes("ADMIN") && (
          <Card className="p-5 flex flex-col items-center justify-center text-center border-border">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <span className="text-xs text-muted-foreground font-semibold block">Total Orders</span>
            <p className="text-2xl font-black text-foreground mt-0.5">{ordersQuery.data?.length || 0}</p>
          </Card>
        )}
      </div>

      {/* Edit Shop & Verification Modal Dialog */}
      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="lg">
        <DialogHeader>
          <div className="text-left space-y-1">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Store className="h-5 w-5 text-primary" /> Update Shop & Verification Details
            </h3>
            <p className="text-xs text-muted-foreground">
              Provide accurate shop location and documents to get verified for wholesale ordering.
            </p>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto px-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name" className="text-xs font-bold">Shop / Business Name</Label>
            <Input
              id="edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="e.g. Sri Balaji Super Market"
              className="h-10 text-sm"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-city" className="text-xs font-bold">City / Market Hub</Label>
              <Input
                id="edit-city"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                placeholder="Bengaluru"
                className="h-10 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-gstin" className="text-xs font-bold">
                GSTIN / Trade Registration <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-gstin"
                value={editGstNumber}
                onChange={(e) => setEditGstNumber(e.target.value.toUpperCase())}
                placeholder="29ABCDE1234F1Z5"
                maxLength={20}
                className="h-10 text-xs font-mono uppercase"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-address" className="text-xs font-bold flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-primary" /> Shop Detailed Address & Landmark
            </Label>
            <Input
              id="edit-address"
              value={editAddressLine}
              onChange={(e) => setEditAddressLine(e.target.value)}
              placeholder="e.g. Shop #12, 1st Cross, Main Road, Bengaluru"
              className="h-10 text-sm"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-language" className="text-xs font-bold flex items-center gap-1.5">
              <Languages className="h-3.5 w-3.5 text-primary" /> Preferred Language (WhatsApp & Calls)
            </Label>
            <select
              id="edit-language"
              value={editLanguagePreference}
              onChange={(e) => setEditLanguagePreference(e.target.value)}
              className="w-full h-10 rounded-xl border border-border bg-background px-3 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">Not Set (Select Preferred Language)</option>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* GPS Pin Capture */}
          <div className="rounded-xl border border-border bg-muted/20 p-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold flex items-center gap-1.5 text-foreground">
                <Navigation className="h-3.5 w-3.5 text-primary" /> Shop GPS Location Pin
              </span>
              {editLatitude !== null && editLongitude !== null ? (
                <span className="text-2xs font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" /> GPS Coordinates Set
                </span>
              ) : (
                <span className="text-2xs text-amber-600 dark:text-amber-400 font-semibold">
                  Required for Driver Navigation
                </span>
              )}
            </div>

            {editLatitude !== null && editLongitude !== null ? (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground font-mono text-2xs truncate">
                  {editLatitude.toFixed(5)}°, {editLongitude.toFixed(5)}°
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCaptureGPS}
                  loading={locating}
                  className="text-2xs font-bold h-7 gap-1"
                >
                  <Navigation className="h-3 w-3 text-primary" /> Update GPS
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCaptureGPS}
                loading={locating}
                className="w-full text-xs font-bold gap-1.5 h-9"
              >
                <Navigation className="h-3.5 w-3.5 text-primary" /> Capture Shop Location (GPS)
              </Button>
            )}

            {locatingMsg && (
              <p className="text-2xs text-muted-foreground">{locatingMsg}</p>
            )}
          </div>

          {/* 1. Storefront Photo */}
          <DocumentUpload
            label="1. Storefront / Signboard Photo"
            subtitle="Shop Board, Physical Frontage, Entrance"
            value={editShopPhoto}
            onSuccess={(url) => setEditShopPhoto(url)}
          />

          {/* 2. Supporting KYC Document */}
          <DocumentUpload
            label="2. Supporting KYC Document (PDF or Image)"
            subtitle="GST Certificate, Trade License, FSSAI, Udhyam Registration"
            value={editDocUrl}
            onSuccess={(url) => setEditDocUrl(url)}
          />
        </div>

        <DialogFooter>
          <div className="flex items-center justify-end gap-2 w-full pt-3 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              className="text-xs font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={updateProfileMutation.isPending}
              onClick={() => updateProfileMutation.mutate()}
              className="text-xs font-bold shadow-md"
            >
              Submit for Verification
            </Button>
          </div>
        </DialogFooter>
      </Dialog>

      {/* Lightbox Modal for Photo / KYC Document Inspection */}
      {isLightboxOpen && activeRetailer?.photoUrl && (
        <ImageLightboxModal
          open={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          photos={[parseRetailerKyc(activeRetailer.photoUrl).shopPhoto || activeRetailer.photoUrl]}
          initialIndex={0}
          title={activeRetailer.name}
          subtitle={`Shop Document · ${activeRetailer.city || 'Bangalore'}`}
        />
      )}
    </div>
  );
}
